import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import {
  ArrowRight, CheckCircle2, Mail, Package, Truck, BarChart2,
  Clock, DollarSign, ShieldCheck, Zap, Award, Settings
} from 'lucide-react';

const MaritimeAI = () => {
  const metrics = [
    { value: '30 Min', label: 'Quote Turnaround', description: 'Reduced from 2–3 days' },
    { value: '$100K+', label: 'Annual Labor Recovered', description: 'Per deployment' },
    { value: 'Zero', label: 'Pricing Errors', description: 'Since go-live' },
    { value: '1 Week', label: 'Average Deployment', description: 'Engagement to go-live' },
  ];

  const services = [
    {
      icon: Mail,
      title: 'AI RFQ Processing & Quote Automation',
      description: 'An AI engine that reads inbound RFQ emails, extracts every line item, matches IMPA/ISSA codes, and generates margin-protected quotes — automatically.',
      outcomes: [
        'AI email parsing engine trained on maritime vocabulary',
        'IMPA and ISSA code matching with 847+ line item capacity',
        'Margin-protected quote generation with configurable rules',
        'PDF quote output ready for customer delivery',
      ],
    },
    {
      icon: Package,
      title: 'Inventory & Catalogue Management',
      description: 'Integrate vendor catalogues and keep pricing synchronised in real time so every quote reflects actual stock and current margins.',
      outcomes: [
        'Vendor catalogue integration and normalisation',
        'Live pricing synchronisation across suppliers',
        'Multi-supplier comparison and margin optimisation',
        'Inventory availability checking before quoting',
      ],
    },
    {
      icon: Truck,
      title: 'Order Management & Fulfilment Automation',
      description: 'Convert accepted quotes to purchase orders, generate warehouse pick lists, and track delivery status — without manual re-entry.',
      outcomes: [
        'Automated RFQ to order conversion on acceptance',
        'Warehouse pick-list generation and routing',
        'Order status tracking and exception alerting',
        'Delivery confirmation workflows and documentation',
      ],
    },
    {
      icon: BarChart2,
      title: 'Analytics & Operational Visibility',
      description: 'A management dashboard showing quote performance, supplier reliability, revenue trends, and operational efficiency across your chandling operation.',
      outcomes: [
        'Quote performance dashboard (conversion rate, turnaround time)',
        'Supplier performance analytics and SLA tracking',
        'Revenue and margin reporting by vessel, port, and period',
        'Operational efficiency metrics and capacity utilisation',
      ],
    },
  ];

  const techStack = [
    { category: 'AI Core', items: ['AI Email Parsing Engine', 'IMPA/ISSA Code Databases', 'NLP Extraction', 'Custom Matching'] },
    { category: 'Quote Generation', items: ['Web-Based Admin Panel', 'Automated Quote Generation', 'PDF Output'] },
    { category: 'Integrations', items: ['Vendor Catalogue APIs', 'Email Integration', 'REST APIs'] },
    { category: 'Infrastructure', items: ['Cloud-Hosted', 'On-Premise Option', '99.9% Uptime SLA'] },
  ];

  const useCases = [
    { name: 'Ship Chandlers & Maritime Distributors', desc: 'Full RFQ-to-delivery automation replacing a manual quoting team and days of back-and-forth email' },
    { name: 'Marine Procurement Teams', desc: 'Centralised procurement platform with supplier comparison, compliance documentation, and audit trails' },
    { name: 'Port Agents & Marine Logistics', desc: 'Rapid quote turnaround and order tracking integrated with port operations and vessel scheduling' },
  ];

  const whyChoose = [
    {
      icon: Award,
      title: 'Maritime-Specific AI',
      description: 'Trained on maritime vocabulary, IMPA codes, and chandling workflows — not a generic AI tool bolted on.',
    },
    {
      icon: Clock,
      title: 'Speed as Competitive Advantage',
      description: '30-minute quote turnaround versus 2–3 days. The first chandler to respond with an accurate quote wins the business.',
    },
    {
      icon: DollarSign,
      title: 'Zero Margin Erosion',
      description: 'Margin rules enforced at the engine level. Every quote is compliant with your pricing policy before it leaves the system.',
    },
    {
      icon: Zap,
      title: 'One Week to Go-Live',
      description: 'Platform validated in parallel with your existing process. Cutover once confidence is established — average one week.',
    },
  ];

  const faqs = [
    {
      q: 'How does the AI handle messy or inconsistent RFQ emails?',
      a: 'Our parsing engine is trained specifically on maritime RFQ vocabulary, including inconsistent product descriptions, abbreviations, and multi-language inputs common in ship chandling. It does not require preprocessing or standard formatting — it handles the real-world variety your team deals with every day.',
    },
    {
      q: 'What happens if a line item cannot be matched?',
      a: 'Unmatched line items are flagged for manual review. The system generates a quote for all successfully matched items and produces a separate exception list for the items requiring human attention. You are never blocked from responding to a customer.',
    },
    {
      q: 'Can you integrate with our existing inventory or accounting system?',
      a: 'Yes. We build REST API integrations as part of the deployment scope. Common integrations include inventory management systems, accounting platforms, and ERP systems. We assess your existing stack during the engagement scoping call.',
    },
    {
      q: 'Is IMPA and ISSA code matching included as standard?',
      a: 'Yes. IMPA and ISSA code matching is a core component of the platform, not an add-on. Every line item is matched against the full IMPA and ISSA code databases during parsing.',
    },
    {
      q: 'What happens to our manual processes during the transition?',
      a: 'We run the platform in parallel with your existing process during a validation period. Your team continues to operate as normal while we validate output accuracy. Cutover only happens when both parties are confident in the system — on average this takes one week.',
    },
  ];

  return (
    <>
      <Helmet>
        <title>AI Automation for Maritime & Ship Chandling Operations | Agility</title>
        <meta
          name="description"
          content="Purpose-built AI automation for ship chandlers and maritime distributors. 30-minute RFQ turnaround, $100K+ annual labor recovered, zero pricing errors. IMPA/ISSA code matching."
        />
        <link rel="canonical" href="https://www.agilitytech.ai/maritime-ai" />
        <meta property="og:type" content="website" />
        <meta property="og:title" content="AI Automation for Maritime & Ship Chandling Operations | Agility" />
        <meta property="og:description" content="Purpose-built AI automation for ship chandlers. 30-minute RFQ turnaround, $100K+ annual labor recovered, zero pricing errors. IMPA/ISSA code matching." />
        <meta property="og:url" content="https://www.agilitytech.ai/maritime-ai" />
        <meta property="og:image" content="https://www.agilitytech.ai/agility hex fonts.png" />
      </Helmet>

      {/* Hero */}
      <section className="relative min-h-[85vh] flex items-center justify-center overflow-hidden bg-gradient-to-br from-cyan-900 via-black to-slate-900">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,_var(--tw-gradient-stops))] from-cyan-500 via-transparent to-transparent" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-32 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-cyan-500/10 border border-cyan-500/30 rounded-full mb-8">
            <Truck className="w-4 h-4 text-cyan-400" />
            <span className="text-cyan-300 text-sm font-medium">Maritime AI Automation</span>
          </div>
          <h1 className="text-5xl md:text-7xl font-bold text-white mb-6 leading-tight">
            AI for Ship Chandlers<br />
            <span className="text-teal-400">& Maritime Distributors</span>
          </h1>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto mb-10 leading-relaxed">
            30-minute RFQ turnaround. $100K+ annual labor recovered. Zero pricing errors.
            Purpose-built AI automation for maritime chandling — deployed in one week.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-16">
            <Link
              to="/contact"
              className="group inline-flex items-center justify-center gap-2 bg-teal-500 hover:bg-teal-600 text-white px-8 py-4 rounded-lg font-semibold transition-all"
            >
              Schedule a Discovery Call
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </Link>
            <Link
              to="/case-studies"
              className="inline-flex items-center justify-center gap-2 bg-white/10 hover:bg-white/20 text-white px-8 py-4 rounded-lg font-semibold border border-white/20 transition-all"
            >
              View Case Studies
            </Link>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 max-w-4xl mx-auto">
            {metrics.map((m, i) => (
              <div key={i} className="text-center">
                <div className="text-4xl font-bold text-teal-400 mb-1">{m.value}</div>
                <div className="text-white font-semibold mb-1">{m.label}</div>
                <div className="text-gray-400 text-sm">{m.description}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              What We Build for Maritime
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Four automation layers that transform your chandling operation from manual to fully automated.
            </p>
          </div>
          <div className="grid md:grid-cols-2 gap-8">
            {services.map((s, i) => {
              const Icon = s.icon;
              return (
                <div key={i} className="bg-gray-50 rounded-xl p-8 border border-gray-200 hover:border-teal-400 transition-all">
                  <Icon className="w-10 h-10 text-teal-600 mb-4" />
                  <h3 className="text-xl font-bold text-gray-900 mb-3">{s.title}</h3>
                  <p className="text-gray-600 mb-5 text-sm leading-relaxed">{s.description}</p>
                  <ul className="space-y-2">
                    {s.outcomes.map((o, j) => (
                      <li key={j} className="flex items-start gap-2 text-sm text-gray-700">
                        <CheckCircle2 className="w-4 h-4 text-teal-600 mt-0.5 flex-shrink-0" />
                        {o}
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Featured Results */}
      <section className="py-20 bg-gradient-to-br from-cyan-700 to-teal-600 text-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold mb-4">Featured Maritime Deployment</h2>
          <p className="text-cyan-100 text-lg mb-10 max-w-2xl mx-auto">
            A leading ship chandler replaced their manual quoting process with Agility's AI platform.
          </p>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            {[
              { value: '30 min', label: 'Quote Turnaround' },
              { value: '$100K+', label: 'Labor Recovered' },
              { value: 'Zero', label: 'Pricing Errors' },
              { value: '847+', label: 'Lines per RFQ' },
              { value: '1 Week', label: 'Deployment Time' },
            ].map((stat, i) => (
              <div key={i} className="bg-white/10 rounded-xl p-4 border border-white/20">
                <div className="text-2xl font-bold text-white mb-1">{stat.value}</div>
                <div className="text-cyan-100 text-xs">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Tech Stack */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Technology Stack</h2>
            <p className="text-lg text-gray-600">Maritime-specific AI at the core, flexible integration layer for your existing systems.</p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {techStack.map((t, i) => (
              <div key={i} className="bg-white rounded-xl p-6 border border-gray-200">
                <div className="text-xs font-bold text-gray-500 uppercase tracking-widest mb-4">{t.category}</div>
                <ul className="space-y-2">
                  {t.items.map((item, j) => (
                    <li key={j} className="flex items-center gap-2 text-gray-800 text-sm font-medium">
                      <div className="w-1.5 h-1.5 bg-teal-500 rounded-full" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Use Cases */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Who We Work With</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {useCases.map((uc, i) => (
              <div key={i} className="bg-gray-50 rounded-xl p-6 border border-gray-200">
                <div className="text-teal-700 font-bold mb-2">{uc.name}</div>
                <p className="text-gray-600 text-sm leading-relaxed">{uc.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose */}
      <section className="py-24 bg-gradient-to-br from-gray-900 to-black text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-4">
              Why Maritime Teams Choose <span className="text-teal-400">Agility</span>
            </h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {whyChoose.map((item, i) => {
              const Icon = item.icon;
              return (
                <div key={i} className="bg-white/5 rounded-xl p-8 border border-white/10 hover:border-teal-500/50 transition-all">
                  <Icon className="w-10 h-10 text-teal-400 mb-4" />
                  <h3 className="text-lg font-bold mb-3">{item.title}</h3>
                  <p className="text-gray-300 text-sm leading-relaxed">{item.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold text-gray-900 mb-12 text-center">
            Frequently Asked Questions
          </h2>
          <div className="space-y-6">
            {faqs.map((f, i) => (
              <div key={i} className="bg-white rounded-xl p-8 border border-gray-200">
                <h3 className="text-lg font-bold text-gray-900 mb-3">{f.q}</h3>
                <p className="text-gray-600 leading-relaxed">{f.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-gradient-to-br from-cyan-700 to-teal-600">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Ready to Automate Your Chandling Operation?
          </h2>
          <p className="text-xl text-white/80 mb-8 max-w-2xl mx-auto">
            Tell us about your current RFQ process. We'll show you what automation looks like for your operation.
          </p>
          <Link
            to="/contact"
            className="inline-flex items-center gap-2 bg-white text-cyan-700 hover:bg-gray-100 px-8 py-4 rounded-lg font-semibold transition-all text-lg"
          >
            Schedule a Discovery Call
            <ArrowRight className="w-5 h-5" />
          </Link>
        </div>
      </section>
    </>
  );
};

export default MaritimeAI;
